/**
 * Pure H OS · Sprint 2 · Notification Drawer.
 *
 * Slide-out on the right, mirroring the Notification Center page's
 * groupings (Unread / Priority / Mentions / AI). Composes Drawer,
 * TabBar, ListRow, SeverityBadge.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AtSign, Sparkles, AlertOctagon, ClipboardCheck, Bell, Check } from "lucide-react";
import { usePlatform } from "../context/PlatformContext";
import { SeverityBadge } from "./lib";
import { Drawer, ListRow, TabBar, Kbd } from "./lib.overlay";
import { Notification, NotificationKind } from "../mocks/platformData";

const KIND_META: Record<NotificationKind, { icon: any; tint: string; fg: string; label: string }> = {
  mention:  { icon: AtSign,        tint: "var(--ph-brand-tint)",  fg: "var(--ph-brand-primary)", label: "Mention"  },
  ai:       { icon: Sparkles,      tint: "var(--ph-ai-tint)",     fg: "var(--ph-ai)",            label: "AI"       },
  priority: { icon: AlertOctagon,  tint: "var(--ph-danger-tint)", fg: "var(--ph-danger)",        label: "Priority" },
  approval: { icon: ClipboardCheck,tint: "var(--ph-warning-tint)",fg: "var(--ph-warning)",       label: "Approval" },
  system:   { icon: Bell,          tint: "var(--ph-neutral-tint)",fg: "var(--ph-ink-muted)",     label: "System"   },
};

type Tab = "unread" | "all" | "priority" | "mentions" | "ai";

export default function NotificationDrawer() {
  const { drawerOpen, closeDrawer, notifications, unreadCount, markRead, markAllRead } = usePlatform();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("unread");

  const filtered = useMemo(() => {
    switch (tab) {
      case "unread":   return notifications.filter((n) => !n.read);
      case "priority": return notifications.filter((n) => n.kind === "priority");
      case "mentions": return notifications.filter((n) => n.kind === "mention");
      case "ai":       return notifications.filter((n) => n.kind === "ai");
      default:         return notifications;
    }
  }, [tab, notifications]);

  const counts = useMemo(
    () => ({
      unread:   notifications.filter((n) => !n.read).length,
      all:      notifications.length,
      priority: notifications.filter((n) => n.kind === "priority").length,
      mentions: notifications.filter((n) => n.kind === "mention").length,
      ai:       notifications.filter((n) => n.kind === "ai").length,
    }),
    [notifications],
  );

  const open = (n: Notification) => {
    markRead(n.id);
    if (n.href) { closeDrawer(); navigate(n.href); }
  };

  return (
    <Drawer open={drawerOpen} onClose={closeDrawer} title="Notifications" testId="notification-drawer" width={440}>
      <div className="mb-4 flex items-center justify-between">
        <TabBar<Tab>
          testId="notif-drawer-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "unread",   label: "Unread",   count: counts.unread   },
            { key: "all",      label: "All",      count: counts.all      },
            { key: "priority", label: "Priority", count: counts.priority },
            { key: "mentions", label: "Mentions", count: counts.mentions },
            { key: "ai",       label: "AI",       count: counts.ai       },
          ]}
        />
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>
          {unreadCount === 0 ? "You're all caught up." : `${unreadCount} unread`}
        </div>
        <button
          data-testid="mark-all-read"
          onClick={markAllRead}
          className="inline-flex items-center gap-1 text-xs font-medium"
          style={{ color: "var(--ph-brand-primary)" }}
        >
          <Check size={12} /> Mark all read
        </button>
      </div>

      <div className="space-y-1" data-testid="notif-drawer-list">
        {filtered.length === 0 ? (
          <div
            className="py-10 text-center text-sm"
            style={{ color: "var(--ph-ink-muted)" }}
            data-testid="notif-drawer-empty"
          >
            Nothing here.
          </div>
        ) : filtered.map((n) => {
          const meta = KIND_META[n.kind];
          const Icon = meta.icon;
          return (
            <ListRow
              key={n.id}
              testId={`notif-row-${n.id}`}
              onClick={() => open(n)}
              icon={
                <span
                  className="inline-flex items-center justify-center"
                  style={{
                    width: 26, height: 26,
                    borderRadius: "999px",
                    background: meta.tint, color: meta.fg,
                  }}
                >
                  <Icon size={13} />
                </span>
              }
              label={
                <span className="inline-flex items-center gap-2">
                  {!n.read && (
                    <span
                      style={{
                        width: 6, height: 6, borderRadius: "999px",
                        background: "var(--ph-brand-primary)",
                        display: "inline-block",
                      }}
                    />
                  )}
                  <span className="truncate">{n.title}</span>
                </span>
              }
              sublabel={
                <span>
                  <span style={{ color: "var(--ph-ink-default)" }}>{n.actor}</span>
                  <span> · {n.timestamp}</span>
                </span>
              }
              trailing={n.severity ? <SeverityBadge severity={n.severity} /> : undefined}
            />
          );
        })}
      </div>

      <div className="mt-5 pt-3 flex items-center justify-between text-[11px]"
        style={{ borderTop: "1px solid var(--ph-border-subtle)", color: "var(--ph-ink-muted)" }}>
        <span className="inline-flex items-center gap-1"><Kbd>N</Kbd> Toggle</span>
        <button
          onClick={() => { closeDrawer(); navigate("/pureh/notifications"); }}
          data-testid="open-notif-center"
          className="font-medium"
          style={{ color: "var(--ph-brand-primary)" }}
        >
          Open Notification Center →
        </button>
      </div>
    </Drawer>
  );
}
